import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  HelpCircle, Plus, Loader2, MessageCircle, Clock, CheckCircle2, 
  AlertCircle, User, Calendar, Tag, ChevronRight, Search, Filter,
  Inbox, CircleDot
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priorityConfig = { 
  low: { label: 'Basse', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: '○' },
  medium: { label: 'Moyenne', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '◐' },
  high: { label: 'Haute', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '◕' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700 border-red-200', icon: '●' }
};

const statusConfig = { 
  open: { label: 'Ouvert', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  resolved: { label: 'Résolu', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  closed: { label: 'Fermé', color: 'bg-slate-100 text-slate-500 border-slate-200' }
};

const categoryConfig = {
  general: { label: 'Général', icon: HelpCircle },
  technical: { label: 'Technique', icon: AlertCircle },
  access: { label: 'Accès', icon: User },
  other: { label: 'Autre', icon: Tag }
};

export default function SupportPage() {
  const { isAdmin } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general' });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets`);
      setTickets(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/tickets`, form);
      toast.success('Ticket créé avec succès');
      setDialogOpen(false);
      setForm({ title: '', description: '', priority: 'medium', category: 'general' });
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (ticket, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tickets/${ticket.id}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchTickets();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Assistance technique</h1>
          <p className="text-slate-500 mt-1">Créez et suivez vos demandes d'aide</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />Nouveau ticket
        </Button>
      </div>

      {/* Stats Cards - New Design */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Inbox className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Total tickets</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow cursor-pointer ${statusFilter === 'open' ? 'ring-2 ring-emerald-200' : ''}`} onClick={() => setStatusFilter('open')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CircleDot className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.open}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Ouverts</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-amber-500 hover:shadow-md transition-shadow cursor-pointer ${statusFilter === 'in_progress' ? 'ring-2 ring-amber-200' : ''}`} onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.in_progress}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">En cours</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-slate-400 hover:shadow-md transition-shadow cursor-pointer ${statusFilter === 'resolved' ? 'ring-2 ring-slate-200' : ''}`} onClick={() => setStatusFilter('resolved')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-slate-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.resolved}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Résolus</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un ticket..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="open">Ouverts</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="resolved">Résolus</SelectItem>
                <SelectItem value="closed">Fermés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      {filteredTickets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-1">Aucun ticket trouvé</h3>
            <p className="text-slate-500">
              {searchQuery || statusFilter !== 'all' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Créez votre premier ticket pour obtenir de l\'aide'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Ticket</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Catégorie</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Priorité</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Statut</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Date</th>
                  {isAdmin && <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTickets.map((ticket) => {
                  const CategoryIcon = categoryConfig[ticket.category]?.icon || HelpCircle;
                  return (
                    <tr 
                      key={ticket.id} 
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            ticket.status === 'open' ? 'bg-emerald-100' : 
                            ticket.status === 'in_progress' ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            <MessageCircle className={`w-5 h-5 ${
                              ticket.status === 'open' ? 'text-emerald-600' : 
                              ticket.status === 'in_progress' ? 'text-blue-600' : 'text-slate-500'
                            }`} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-slate-800 truncate">{ticket.title}</h3>
                            <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <User className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-400">{ticket.user_name}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">{categoryConfig[ticket.category]?.label || ticket.category}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={`${priorityConfig[ticket.priority]?.color} border`}>
                          <span className="mr-1">{priorityConfig[ticket.priority]?.icon}</span>
                          {priorityConfig[ticket.priority]?.label}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={`${statusConfig[ticket.status]?.color} border`}>
                          {statusConfig[ticket.status]?.label}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(ticket.created_at), 'dd MMM yyyy', { locale: fr })}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                          <Select value={ticket.status} onValueChange={(v) => handleStatusChange(ticket, v)}>
                            <SelectTrigger className="w-32 h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Ouvert</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="resolved">Résolu</SelectItem>
                              <SelectItem value="closed">Fermé</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              Nouveau ticket
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre <span className="text-red-500">*</span></Label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm({ ...form, title: e.target.value })} 
                placeholder="Résumé du problème" 
              />
            </div>
            <div className="space-y-2">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea 
                value={form.description} 
                onChange={(e) => setForm({ ...form, description: e.target.value })} 
                rows={4} 
                placeholder="Décrivez votre problème en détail..." 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">○ Basse</SelectItem>
                    <SelectItem value="medium">◐ Moyenne</SelectItem>
                    <SelectItem value="high">◕ Haute</SelectItem>
                    <SelectItem value="urgent">● Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Général</SelectItem>
                    <SelectItem value="technical">Technique</SelectItem>
                    <SelectItem value="access">Accès</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer le ticket
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <DialogTitle className="pr-8">{selectedTicket.title}</DialogTitle>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={`${statusConfig[selectedTicket.status]?.color} border`}>
                    {statusConfig[selectedTicket.status]?.label}
                  </Badge>
                  <Badge className={`${priorityConfig[selectedTicket.priority]?.color} border`}>
                    {priorityConfig[selectedTicket.priority]?.icon} {priorityConfig[selectedTicket.priority]?.label}
                  </Badge>
                  <Badge variant="outline">
                    {categoryConfig[selectedTicket.category]?.label}
                  </Badge>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedTicket.user_name}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(selectedTicket.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
